/**
 * AgentRunsDashboardPage — global approvals queue + run history.
 *
 * Shows all agent runs across all tickets, filtered by status.
 * Pending approvals are surfaced at the top of the default view.
 * Approval/reject/stop actions require the agents_approve permission.
 */
import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Link, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { agentRunsApi, agentsApi, connectionsApi, ticketsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { useNotification } from "../context/NotificationContext";
import { REJECT_QUICK_REASONS as _REJECT_QUICK_REASONS } from "../utils/agentRunConstants";
import { classifySource, SOURCE_STATES } from "../components/statusVocabulary";
import { classifySourceTruth } from "../components/sourceTruth";
import SourceTruthBadge from "../components/SourceTruthBadge";
import { useOnlineStatus } from "../components/useLiveState";
import RunCostChips from "../components/RunCostChips";
import { DEGRADED_ANALYSES_AGGREGATE_LABEL, degradedAnalysisLabel } from "../components/degradedAnalysisVocabulary";
import { actionTypeLabel } from "../components/actionTypeVocabulary";
import { RUN_STATUS_HE, runStatusLabel } from "../components/runStatusVocabulary";
import { TICKET_STATUS_HE, TICKET_STATUS_EDIT_ORDER } from "../components/ticketStatusVocabulary";
import { complexityLabel, complexityTone, COMPLEXITY_ORDER, COMPLEXITY_TONE_UNKNOWN } from "../components/complexityVocabulary";
import { taskTypeLabel } from "../components/taskTypeVocabulary";

// ─── Constants ───────────────────────────────────────────────────────────────

// This page's own classes for the complexity scale; the vocabulary module only says which arm was
// read. A value the scale does not hold gets a neutral class rather than the catch-all green the
// ternaries here used to fall through to — and the two strips below now draw the same tone as the
// row cell, which they did not when each arm was re-decided inline.
const COMPLEXITY_TEXT = {
  low:     "text-green-500",
  medium:  "text-amber-500",
  high:    "text-red-500",
  [COMPLEXITY_TONE_UNKNOWN]: "text-gray-400",
};
const COMPLEXITY_STRIP = {
  low:     "bg-green-50 border-green-100",
  medium:  "bg-amber-50 border-amber-100",
  high:    "bg-red-50 border-red-100",
  [COMPLEXITY_TONE_UNKNOWN]: "bg-gray-50 border-gray-200",
};
const COMPLEXITY_STRIP_TEXT = {
  low:     "text-green-700",
  medium:  "text-amber-700",
  high:    "text-red-700",
  [COMPLEXITY_TONE_UNKNOWN]: "text-gray-600",
};
const COMPLEXITY_STRIP_WORD = {
  low:     "text-green-500",
  medium:  "text-amber-500",
  high:    "text-red-500",
  [COMPLEXITY_TONE_UNKNOWN]: "text-gray-500",
};

const STATUS_BADGE = {
  pending:                         "bg-violet-100 text-violet-700",
  analyzing:                       "bg-violet-100 text-violet-700",
  awaiting_technician_review:      "bg-amber-100 text-amber-700",
  awaiting_approval:               "bg-amber-100 text-amber-700",
  waiting_for_customer_connection: "bg-blue-100 text-blue-700",
  connection_active:               "bg-green-100 text-green-700",
  diagnosing:                      "bg-violet-100 text-violet-700",
  paused:                          "bg-gray-200 text-gray-600",
  completed:                       "bg-green-100 text-green-700",
  resolved:                        "bg-green-100 text-green-700",
  failed:                          "bg-red-100 text-red-700",
  rejected:                        "bg-red-100 text-red-700",
};

const ACTIVE_STATUSES = new Set([
  "pending", "analyzing", "diagnosing", "connection_active", "executing",
]);

const REVIEW_STATUSES = new Set([
  "awaiting_technician_review", "awaiting_approval",
]);

const FILTER_OPTIONS = [
  { value: "",                               label: "כל הריצות" },
  { value: "pending_review",                 label: "ממתינים לסקירה (כל)" },
  { value: "awaiting_technician_review",     label: "ממתין לסקירה" },
  { value: "awaiting_approval",              label: "ממתין לאישור" },
  { value: "waiting_for_customer_connection", label: "ממתין לחיבור" },
  { value: "paused",                         label: "הופסק" },
  { value: "completed",                      label: "הושלם" },
  { value: "resolved",                       label: "נפתרה" },
  { value: "failed",                         label: "נכשל" },
  { value: "rejected",                       label: "נדחה" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(new Date(iso));
}

function fmtDuration(ms) {
  if (ms == null) return null;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}ש`;
  return `${Math.round(ms / 60000)}ד`;
}

function badgeClass(status) {
  return STATUS_BADGE[status] ?? "bg-gray-100 text-gray-600";
}

function elapsedMinutes(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
}

function ElapsedBadge({ iso }) {
  const mins = elapsedMinutes(iso);
  if (mins == null) return null;
  const label = mins < 60 ? `${mins}ד` : `${Math.floor(mins / 60)}ש${mins % 60 > 0 ? `${mins % 60}ד` : ""}`;
  const cls = mins >= 30
    ? "text-red-600 font-semibold"
    : mins >= 15
    ? "text-amber-600"
    : "text-gray-400";
  return <span className={`text-xs ${cls}`}>{label} ⏳</span>;
}

// ─── RejectModal ─────────────────────────────────────────────────────────────

function RejectModal({ runId, onDone, onCancel, historicalReasons }) {
  const [reason, setReason] = useState("");
  const qc = useQueryClient();

  const { addNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: () => agentRunsApi.reject(runId, reason || undefined),
    onSuccess: () => {
      addNotification("הריצה נדחתה", "info");
      qc.invalidateQueries({ queryKey: ["agentRuns"] });
      // The three independent pending-count badges (nav, Dashboard, AgentsPage) poll agentsApi.list()
      // under their own query keys with no shared prefix.
      qc.invalidateQueries({ queryKey: ["agents-layout-badge"] });
      qc.invalidateQueries({ queryKey: ["agents-dash"] });
      qc.invalidateQueries({ queryKey: ["agents"] });
      onDone();
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בדחיית הריצה", "error"),
  });

  // Merge presets with top historical reasons (de-duped, presets first)
  const presetSet = new Set(_REJECT_QUICK_REASONS);
  const dynamicReasons = historicalReasons
    ? Object.entries(historicalReasons)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 6)
        .map(([r]) => r)
        .filter((r) => !presetSet.has(r))
    : [];
  const allChips = [..._REJECT_QUICK_REASONS, ...dynamicReasons];

  // No Escape handler previously -- a keyboard user had to tab to the "ביטול" button to dismiss.
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" dir="rtl">
      <div role="dialog" aria-modal="true" aria-labelledby="reject-modal-title" className="bg-white rounded-xl shadow-xl border p-5 w-[26rem] space-y-4">
        <h3 id="reject-modal-title" className="text-sm font-semibold text-red-700">דחיית ניתוח AI</h3>
        <div className="flex flex-wrap gap-1.5">
          {allChips.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setReason(r)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                reason === r
                  ? "bg-red-100 border-red-400 text-red-700 font-medium"
                  : presetSet.has(r)
                    ? "bg-gray-50 border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600"
                    : "bg-amber-50 border-amber-200 text-amber-700 hover:border-red-300 hover:text-red-600"
              }`}
              title={presetSet.has(r) ? undefined : `שימוש תכוף: ${historicalReasons?.[r] ?? 0} פעמים`}
            >
              {r}
            </button>
          ))}
        </div>
        <textarea
          rows={3}
          placeholder="סיבת הדחייה (אופציונלי)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
        {mutation.isError && (
          <p className="text-xs text-red-600">{mutation.error?.response?.data?.detail || "שגיאה"}</p>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {mutation.isPending ? "שולח..." : "דחה"}
          </button>
          <button type="button" onClick={onCancel} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
}

const _ACTIVITY_RE = /^\[(customer|technician|note|ai_agent)\](?:\s*\([^)]*\))?\s*([\s\S]*)$/;

function lastCustomerMessage(recentActivity) {
  if (!recentActivity || typeof recentActivity !== "string") return null;
  const lines = recentActivity.split("\n").filter(Boolean);
  for (let i = lines.length - 1; i >= 0; i--) {
    const m = lines[i].match(_ACTIVITY_RE);
    if (m && m[1] === "customer") return m[2].trim();
  }
  return null;
}

// ─── RunRow ──────────────────────────────────────────────────────────────────

function RunRow({ run, onAction, canApprove, canRun, returnTo }) {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [draftExpanded, setDraftExpanded] = useState(false);
  // null = the technician has not touched the draft. The row stays mounted while a run moves from "analyzing" to "awaiting review", so a
  // copy of the draft taken at mount would be "" for such a run: the textarea would open empty and the row approve would read as "edited".
  const [draftEdit, setDraftEdit] = useState(null);
  const editedDraft = draftEdit ?? (run.analysis?.customer_reply_draft || "");
  const isReview = REVIEW_STATUSES.has(run.status);
  // The analysis' provenance, read by every cell that makes a claim out of it. On the pilot default
  // (no ANALYSIS_PROVIDER) the run's analysis is the deterministic mock, whose confidence is derived
  // from ticket richness and capped at 70 — so a bare figure reads as a real "medium" signal rather
  // than as "nothing ran" (`helpdesk_analyst.py`: MockAnalysisAdapter).
  const degradedLabel = degradedAnalysisLabel(run.analysis?.degraded);
  const isActive = ACTIVE_STATUSES.has(run.status);
  const isFailed = run.status === "failed";
  const _proposedActionType = run.analysis?.proposed_action?.action_type;
  const hasDraftPreview = isReview
    && (_proposedActionType === "send_customer_message" || _proposedActionType === "close_ticket" || _proposedActionType === "escalate")
    && !!run.analysis?.customer_reply_draft;
  const hasCommentPreview = isReview
    && _proposedActionType === "add_comment"
    && !!run.analysis?.proposed_action?.description;
  const hasExpandable = hasDraftPreview || hasCommentPreview;
  // The row's plain "אשר" sends the ORIGINAL AI draft: while the technician has edited the draft in the expander, that click would
  // silently discard the edits and mail the unedited text to the customer. The edited text goes out only via "✓ אשר עם עריכות".
  const draftDirty = canApprove && hasDraftPreview && _proposedActionType === "send_customer_message"
    && editedDraft.trim() !== (run.analysis.customer_reply_draft || "").trim();

  const approveMutation = useMutation({
    mutationFn: () => agentRunsApi.approve(run.id),
    onSuccess: () => {
      addNotification("הריצה אושרה ✓", "success");
      qc.invalidateQueries({ queryKey: ["agentRuns"] });
      // The three independent pending-count badges (nav, Dashboard, AgentsPage) poll agentsApi.list()
      // under their own query keys with no shared prefix.
      qc.invalidateQueries({ queryKey: ["agents-layout-badge"] });
      qc.invalidateQueries({ queryKey: ["agents-dash"] });
      qc.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה באישור הריצה", "error"),
  });

  const feedbackMutation = useMutation({
    mutationFn: () => agentRunsApi.feedback(run.id, {
      edit_type: "reply_edit",
      original_ai_output: { customer_reply_draft: run.analysis?.customer_reply_draft || "" },
      technician_edited_output: { customer_reply_draft: editedDraft.trim() },
    }),
    onSuccess: () => {
      addNotification("הריצה אושרה עם עריכות ✓", "success");
      qc.invalidateQueries({ queryKey: ["agentRuns"] });
      // Same transition as a plain approve (submit_feedback also fires event="approve" on the
      // backend), so the same three independent pending-count badges need the same refresh.
      qc.invalidateQueries({ queryKey: ["agents-layout-badge"] });
      qc.invalidateQueries({ queryKey: ["agents-dash"] });
      qc.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה באישור עם עריכות", "error"),
  });

  const stopMutation = useMutation({
    mutationFn: () => agentRunsApi.stop(run.id),
    onSuccess: () => {
      addNotification("הריצה נעצרה", "info");
      qc.invalidateQueries({ queryKey: ["agentRuns"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעצירת הריצה", "error"),
  });

  const retryMutation = useMutation({
    mutationFn: () => agentRunsApi.retry(run.id),
    onSuccess: () => {
      addNotification("ריצה חדשה נוצרה לניסיון חוזר", "success");
      qc.invalidateQueries({ queryKey: ["agentRuns"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בניסיון חוזר", "error"),
  });

  const rerunMutation = useMutation({
    mutationFn: () => agentRunsApi.start(run.ticket_id, run.agent_id),
    onSuccess: () => {
      addNotification("ניתוח AI חדש הופעל", "success");
      qc.invalidateQueries({ queryKey: ["agentRuns"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בהפעלת ניתוח חוזר", "error"),
  });

  return (
    <>
    <tr className="border-b last:border-0 hover:bg-gray-50 text-sm" dir="rtl">
      {/* Ticket */}
      <td className="px-4 py-3">
        {run.ticket_number ? (
          <Link
            to={`/tickets/${run.ticket_id}?from=${encodeURIComponent(returnTo)}`}
            className="text-brand-600 hover:underline font-medium"
          >
            #{run.ticket_number}
          </Link>
        ) : (
          <span className="text-gray-400">—</span>
        )}
        {run.ticket_priority === "high" && (
          <span className="block text-xs text-red-600 font-semibold mt-0.5" title="עדיפות גבוהה">⚠ גבוהה</span>
        )}
        {run.ticket_is_sla_breach && (
          <span className="block text-xs text-orange-600 font-semibold mt-0.5" title="הפרת SLA — קריאה פתוחה ללא טכנאי מעל 6 שעות">⏰ SLA</span>
        )}
        {run.ticket_status === "closed" && (
          <span className="block text-xs text-gray-400 mt-0.5" title="קריאה סגורה">✓ סגורה</span>
        )}
        {run.ticket_status === "waiting" && (
          <span className="block text-xs text-blue-400 mt-0.5" title="ממתין לגורם חיצוני">⏳ ממתין</span>
        )}
        {run.initiated_by === "auto:email_reply" && (
          <span className="block text-xs text-blue-500 mt-0.5" title="הופעל אוטומטית עקב תגובת לקוח">
            ↩ תגובה
          </span>
        )}
        {run.initiated_by === "auto:new_ticket" && (
          <span className="block text-xs text-emerald-600 mt-0.5" title="הופעל אוטומטית על קריאה חדשה">
            ⚡ חדש
          </span>
        )}
        {(run.initiated_by === "auto:scheduled_check" || run.context?.system_check) && (
          <span className="block text-xs text-violet-600 mt-0.5" title={`בדיקה מתוזמנת${run.context?.check_name ? `: ${run.context.check_name}` : ""}${run.initiated_by !== "auto:scheduled_check" ? " (הופעלה מחדש)" : ""}`}>
            🕐 מתוזמן
          </span>
        )}
      </td>

      {/* Ticket title + AI likely issue */}
      <td className="px-4 py-3 max-w-[220px]">
        <span className="block truncate text-gray-700" title={run.ticket_title}>
          {run.ticket_title ?? "—"}
        </span>
        {run.ticket_company_name && (
          <span className="block truncate text-xs text-gray-400 mt-0.5" title={run.ticket_company_name}>
            {run.ticket_company_name}
          </span>
        )}
        {run.ticket_technician_name && (
          <span className="block truncate text-xs text-gray-400 mt-0.5" title={`טכנאי: ${run.ticket_technician_name}`}>
            👤 {run.ticket_technician_name}
          </span>
        )}
        {isReview && run.initiated_by === "auto:email_reply" && (() => {
          const msg = lastCustomerMessage(run.context?.recent_activity);
          return msg ? (
            <span
              className="block truncate text-xs text-blue-600 mt-0.5"
              title={msg}
            >
              ↩ "{msg.length > 80 ? msg.slice(0, 80) + "…" : msg}"
            </span>
          ) : null;
        })()}
        {/* The provenance of the analysis this row's AI lines describe, read before every one of them.
            On the pilot default (no ANALYSIS_PROVIDER) the run's analysis is the deterministic mock,
            whose likely_issue names the ticket's own title, whose confidence is derived from ticket
            richness and whose task_type/complexity are literals it writes for every ticket — a
            classification of no ticket in particular. It heads this cell on every row rather than only
            on a review row, because this cell is where the finding is read and it is the first cell to
            print an AI-derived value on either arm: a non-review row draws no finding *sentence*, but it
            does print the classification line below, and the confidence figure in the column that
            follows. Drawing it in the confidence column instead put the mock's two constants ahead of
            the sentence that qualifies them, on the arm where nothing else qualifies them. */}
        {degradedLabel && (
          <span
            data-testid={`run-ai-degraded-${run.id}`}
            className="inline-block text-[10px] font-medium leading-none bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5 mt-1"
          >
            {degradedLabel}
          </span>
        )}
        {isReview && run.analysis?.likely_issue && (
          <span
            className="block truncate text-xs text-amber-700 mt-0.5"
            title={run.analysis.likely_issue}
          >
            🤖 {run.analysis.likely_issue}
          </span>
        )}
        {isReview && run.analysis?.proposed_action && (
          <span className="block truncate text-xs text-blue-600 mt-0.5"
            title={run.analysis.proposed_action.description}>
            ⚡ {actionTypeLabel(run.analysis.proposed_action.action_type)}
            {run.analysis.proposed_action.action_type === "escalate" && run.analysis.proposed_action.target && (
              <span className="mr-1 text-gray-400">→ {run.analysis.proposed_action.target}</span>
            )}
          </span>
        )}
        {isReview && !run.analysis?.proposed_action && run.analysis?.missing_information?.length > 0 && (
          <span
            className="block truncate text-xs text-gray-400 mt-0.5"
            title={run.analysis.missing_information.join(" · ")}
          >
            ❓ חסר: {run.analysis.missing_information[0]}
            {run.analysis.missing_information.length > 1 && ` (+${run.analysis.missing_information.length - 1})`}
          </span>
        )}
        {run.analysis?.task_type && (
          <span data-testid={`run-classification-${run.id}`} className="inline-flex items-center gap-1 mt-0.5">
            <span className="text-xs text-indigo-500 font-medium">{taskTypeLabel(run.analysis.task_type)}</span>
            {run.analysis.complexity && (
              <span
                className={`text-xs font-medium ${COMPLEXITY_TEXT[complexityTone(run.analysis.complexity)]}`}
              >· {complexityLabel(run.analysis.complexity)}</span>
            )}
          </span>
        )}
        {hasExpandable && (
          <button
            type="button"
            onClick={() => setDraftExpanded((v) => !v)}
            className="block text-xs text-blue-500 hover:text-blue-700 mt-1"
          >
            {draftExpanded
              ? "▲ הסתר"
              : hasDraftPreview
                ? ({
                    close_ticket: "▼ הצג מייל סגירה",
                    escalate:     "▼ הצג מסר ללקוח",
                  }[_proposedActionType] ?? "▼ הצג טיוטה")
                : "▼ הצג הערה"}
          </button>
        )}
        {!isReview && run.context?.execution_result && (
          <span className={`block truncate text-xs mt-0.5 ${
            run.context.execution_result.success && run.context.execution_result.executed
              ? "text-green-700"
              : run.context.execution_result.success
              ? "text-gray-400"
              : "text-red-600"
          }`} title={run.context.execution_result.message}>
            {run.context.execution_result.success && run.context.execution_result.executed ? "✓ " : run.context.execution_result.success ? "— " : "✗ "}
            {{
              add_comment:           "הערה נוספה",
              close_ticket:          "קריאה נסגרה",
              escalate:              "הועבר לגורם אחר",
              send_customer_message: "מייל נשלח ללקוח",
            }[run.context.execution_result.action_type] ?? run.context.execution_result.action_type ?? "בוצע"}
          </span>
        )}
        {run.status === "rejected" && run.context?.reject_reason && (
          <span
            className="block truncate text-xs text-red-500 mt-0.5"
            title={run.context.reject_reason}
          >
            ✗ {run.context.reject_reason}
          </span>
        )}
        {(run.status === "completed" || run.status === "resolved") && run.result_summary && (
          <span
            className="block truncate text-xs text-gray-500 mt-0.5"
            title={run.result_summary}
          >
            {run.result_summary}
          </span>
        )}
      </td>

      {/* Agent */}
      <td className="px-4 py-3">
        {run.agent_name ? (
          <Link to={`/agents/${run.agent_id}`} className="text-xs text-brand-600 hover:underline truncate block max-w-[120px]">
            {run.agent_name}
          </Link>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          {isActive && (
            <div className="h-2 w-2 rounded-full bg-violet-500 animate-pulse shrink-0" />
          )}
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass(run.status)}`}>
            {runStatusLabel(run.status)}
          </span>
        </div>
      </td>

      {/* Confidence */}
      <td className="px-4 py-3 text-gray-600">
        {/* The figure's disclosure is the row's own, drawn in the נושא cell — the first cell to print an
            AI-derived value, so it is read before this figure as well as before the classification. The
            column does not repeat the sentence. The figure itself stays: disclosed, not censored. */}
        {run.analysis?.confidence != null ? (
          <span>
            {run.analysis.confidence}%
            {/* Both badges are verdicts *about the figure*, and one of them recommends approving the
                run. A fallback's derived number cannot support either: on the pilot default the mock
                would otherwise print "בטחון AI גבוה — מומלץ לאשר" two cells from this row's own
                approve button, on a row that says in the same breath that nothing real analysed it. */}
            {isReview && !degradedLabel && run.analysis.confidence >= 80 && (
              <span
                className="block text-xs text-green-600 font-semibold mt-0.5"
                title="בטחון AI גבוה — מומלץ לאשר"
              >
                ✓ גבוה
              </span>
            )}
            {isReview && !degradedLabel && run.analysis.confidence < 60 && (
              <span
                className="block text-xs text-amber-600 font-semibold mt-0.5"
                title="בטחון AI נמוך — דרוש מידע נוסף"
              >
                ⚠ נמוך
              </span>
            )}
            {run.analysis?.risk_level === "red" && (
              <span className="block text-xs text-red-600 font-semibold mt-0.5" title="סיכון AI: גבוה">🔴 סיכון גבוה</span>
            )}
            {run.analysis?.risk_level === "amber" && (
              <span className="block text-xs text-amber-500 font-semibold mt-0.5" title="סיכון AI: בינוני">🟡 סיכון בינוני</span>
            )}
          </span>
        ) : "—"}
      </td>

      {/* Learning signal */}
      <td className="px-4 py-3 text-xs">
        {run.feedback_count > 0 ? (
          <span className="text-indigo-700 font-medium" title="כולל תיקון טכנאי שנשמר ללמידה">
            {run.feedback_count} תיקונים
          </span>
        ) : <span className="text-gray-400">—</span>}
      </td>

      {/* Started / Duration */}
      <td className="px-4 py-3 text-gray-500 text-xs">
        <div>{fmtDate(run.created_at)}</div>
        {fmtDuration(run.duration_ms) && (
          <div className="text-gray-400 mt-0.5">{fmtDuration(run.duration_ms)}</div>
        )}
        {isReview && <div className="mt-0.5"><ElapsedBadge iso={run.updated_at} /></div>}
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {canApprove && isReview && (
            <>
              <button
                type="button"
                onClick={() => approveMutation.mutate()}
                disabled={approveMutation.isPending || draftDirty}
                title={draftDirty ? "ערכת את הטיוטה — השתמש ב'אשר עם עריכות' או באיפוס" : undefined}
                className="bg-green-600 hover:bg-green-700 text-white px-2.5 py-1 rounded text-xs font-medium disabled:opacity-50"
              >
                אשר
              </button>
              <button
                type="button"
                onClick={() => onAction("reject", run.id)}
                className="bg-red-500 hover:bg-red-600 text-white px-2.5 py-1 rounded text-xs font-medium"
              >
                דחה
              </button>
            </>
          )}
          {canApprove && (isActive || isReview) && (
            <button
              type="button"
              onClick={() => stopMutation.mutate()}
              disabled={stopMutation.isPending}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-2.5 py-1 rounded text-xs font-medium disabled:opacity-50"
            >
              עצור
            </button>
          )}
          {/* retry and re-analyse start a new run: the backend needs agents_run for both, stop/approve need agents_approve */}
          {canRun && isFailed && (
            <button
              type="button"
              onClick={() => retryMutation.mutate()}
              disabled={retryMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded text-xs font-medium disabled:opacity-50"
            >
              {retryMutation.isPending ? "..." : "נסה שוב"}
            </button>
          )}
          {canRun && run.status === "rejected" && (
            <button
              type="button"
              onClick={() => rerunMutation.mutate()}
              disabled={rerunMutation.isPending}
              className="border border-brand-300 text-brand-600 hover:bg-brand-50 px-2.5 py-1 rounded text-xs font-medium disabled:opacity-50"
            >
              {rerunMutation.isPending ? "..." : "נתח מחדש"}
            </button>
          )}
          <Link
            to={`/tickets/${run.ticket_id}?from=${encodeURIComponent(returnTo)}`}
            className="text-xs text-brand-600 hover:underline"
          >
            קריאה
          </Link>
          <Link
            to={`/agent-runs/${run.id}`}
            state={{ from: returnTo }}
            className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
          >
            פרטים
          </Link>
        </div>
        {(approveMutation.isError || stopMutation.isError || retryMutation.isError || rerunMutation.isError) && (
          <p className="text-xs text-red-600 mt-0.5">
            {(approveMutation.error || stopMutation.error || retryMutation.error || rerunMutation.error)
              ?.response?.data?.detail ?? "שגיאה"}
          </p>
        )}
      </td>
    </tr>
    {hasExpandable && draftExpanded && (
      <tr className={`border-b ${hasDraftPreview ? "bg-blue-50 border-blue-100" : "bg-amber-50 border-amber-100"}`} dir="rtl">
        <td colSpan="7" className="px-6 pb-3 pt-1">
          {hasDraftPreview && _proposedActionType === "send_customer_message" && canApprove ? (
            <>
              <textarea
                rows={6}
                value={editedDraft}
                onChange={(e) => setDraftEdit(e.target.value)}
                className="w-full text-xs text-gray-700 font-mono leading-relaxed bg-white rounded p-2 border border-blue-200 resize-y"
                dir="auto"
              />
              {editedDraft.trim() !== (run.analysis.customer_reply_draft || "").trim() ? (
                <div className="flex items-center gap-2 mt-1.5">
                  <button
                    type="button"
                    onClick={() => feedbackMutation.mutate()}
                    disabled={feedbackMutation.isPending || !editedDraft.trim()}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-medium disabled:opacity-50"
                  >
                    {feedbackMutation.isPending ? "מאשר..." : "✓ אשר עם עריכות"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDraftEdit(null)}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    איפוס
                  </button>
                  {feedbackMutation.isError && (
                    <span className="text-xs text-red-600">{feedbackMutation.error?.response?.data?.detail || "שגיאה"}</span>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-400 mt-1">ערוך את הטיוטה לעיל כדי לאשר עם שינויים</p>
              )}
            </>
          ) : (
            <p className={`text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed bg-white rounded p-2 max-h-40 overflow-y-auto ${hasDraftPreview ? "border border-blue-100" : "border border-amber-100"}`}>
              {hasDraftPreview
                ? run.analysis.customer_reply_draft
                : run.analysis.proposed_action.description}
            </p>
          )}
        </td>
      </tr>
    )}
    </>
  );
}

// ─── AgentRunsDashboardPage ───────────────────────────────────────────────────

const PAGE_SIZE = 50;
// The backend caps: GET /agent-runs limit <= 200 (a larger request is a 422 that replaced the whole list with
// "cannot load"), and batch-approve takes at most 50 run ids.
const MAX_RUNS_LIMIT = 200;
const SKIP_REASON_LABEL = {
  not_found: "הריצה לא נמצאה",
  wrong_status: "הסטטוס השתנה",
  external_action: "פעולה מול לקוח דורשת אישור ידני",
  self_approval: "לא ניתן לאשר ריצה שהתחלת בעצמך",
  error: "שגיאה בביצוע",
};
const BATCH_APPROVE_MAX = 50;

const EDIT_TYPE_LABELS = {
  analysis_edit: "אבחון",
  reply_edit:    "תגובה",
  action_edit:   "פעולה",
  closing_edit:  "סגירה",
  question_edit: "שאלה",
};

const PERIOD_OPTIONS = [
  { value: "",   label: "כל הזמן" },
  { value: "1",  label: "היום" },
  { value: "7",  label: "7 ימים אחרונים" },
  { value: "30", label: "30 ימים אחרונים" },
  { value: "90", label: "3 חודשים" },
];

export default function AgentRunsDashboardPage() {
  const { can: canAgent } = useAgentPermissions();
  const canApprove = canAgent("agents_approve");
  const canRun = canAgent("agents_run");
  const { addNotification } = useNotification();
  const isOnline = useOnlineStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [agentFilter, setAgentFilter] = useState(searchParams.get("agent_id") || "");
  const [sinceDays, setSinceDays] = useState(searchParams.get("since_days") || "");
  const [ticketNumInput, setTicketNumInput] = useState(searchParams.get("ticket_number") || "");
  const [ticketNumFilter, setTicketNumFilter] = useState(() => {
    const v = parseInt(searchParams.get("ticket_number"), 10);
    return isNaN(v) ? null : v;
  });
  const [titleSearch, setTitleSearch] = useState(searchParams.get("ticket_title") || "");
  const [initiatedByFilter, setInitiatedByFilter] = useState(searchParams.get("initiated_by_type") || "");
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get("ticket_priority") || "");
  const [ticketStatusFilter, setTicketStatusFilter] = useState(searchParams.get("ticket_status") || "");
  const [taskTypeFilter, setTaskTypeFilter] = useState(searchParams.get("task_type") || "");
  const [confidenceMax, setConfidenceMax] = useState(() => {
    const v = parseInt(searchParams.get("confidence_max"), 10);
    return isNaN(v) ? null : v;
  });
  const [confidenceMin, setConfidenceMin] = useState(() => {
    const v = parseInt(searchParams.get("confidence_min"), 10);
    return isNaN(v) ? null : v;
  });
  const [sortBy, setSortBy] = useState(searchParams.get("sort_by") || "");
  const [hasFeedback, setHasFeedback] = useState(searchParams.get("has_feedback") === "true");
  const [myTicketsOnly, setMyTicketsOnly] = useState(searchParams.get("my_tickets_only") === "true");
  const [companyIdFilter, setCompanyIdFilter] = useState(searchParams.get("company_id") || "");
  const [technicianIdFilter, setTechnicianIdFilter] = useState(searchParams.get("technician_id") || "");
  const [rejectRunId, setRejectRunId] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const qcMain = useQueryClient();

  const batchApproveMutation = useMutation({
    mutationFn: (run_ids) => agentRunsApi.batchApprove(run_ids),
    onSuccess: (result) => {
      qcMain.invalidateQueries({ queryKey: ["agentRuns"] });
      qcMain.invalidateQueries({ queryKey: ["agentRunStats"] });
      // The three independent pending-count badges (nav, Dashboard, AgentsPage) poll agentsApi.list()
      // under their own query keys with no shared prefix.
      qcMain.invalidateQueries({ queryKey: ["agents-layout-badge"] });
      qcMain.invalidateQueries({ queryKey: ["agents-dash"] });
      qcMain.invalidateQueries({ queryKey: ["agents"] });
      setBatchResult(result);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה באישור מרובה", "error"),
  });

  // Reset limit when filters change
  const resetLimit = useCallback(() => setLimit(PAGE_SIZE), []);

  // Sync filter state to URL so bookmarks / back-button / refresh work.
  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (agentFilter) params.set("agent_id", agentFilter);
    if (sinceDays) params.set("since_days", sinceDays);
    if (ticketNumFilter != null) params.set("ticket_number", String(ticketNumFilter));
    if (titleSearch.trim()) params.set("ticket_title", titleSearch.trim());
    if (initiatedByFilter) params.set("initiated_by_type", initiatedByFilter);
    if (priorityFilter) params.set("ticket_priority", priorityFilter);
    if (ticketStatusFilter) params.set("ticket_status", ticketStatusFilter);
    if (taskTypeFilter) params.set("task_type", taskTypeFilter);
    if (confidenceMax != null) params.set("confidence_max", String(confidenceMax));
    if (confidenceMin != null) params.set("confidence_min", String(confidenceMin));
    if (sortBy) params.set("sort_by", sortBy);
    if (hasFeedback) params.set("has_feedback", "true");
    if (myTicketsOnly) params.set("my_tickets_only", "true");
    if (companyIdFilter) params.set("company_id", companyIdFilter);
    if (technicianIdFilter) params.set("technician_id", technicianIdFilter);
    setSearchParams(params, { replace: true });
  }, [statusFilter, agentFilter, sinceDays, ticketNumFilter, titleSearch, initiatedByFilter, priorityFilter, ticketStatusFilter, taskTypeFilter, confidenceMax, confidenceMin, sortBy, hasFeedback, myTicketsOnly, companyIdFilter, technicianIdFilter, setSearchParams]);

  const { data: agents = [] } = useQuery({
    queryKey: ["agents-dash-filter"],
    queryFn: agentsApi.list,
    staleTime: 60_000,
  });

  const { data: connHealth } = useQuery({
    queryKey: ["connectionsHealth-dash"],
    queryFn: connectionsApi.health,
    staleTime: 300_000,
  });

  const runsQuery = useQuery({
    queryKey: ["agentRuns", statusFilter, agentFilter, ticketNumFilter, titleSearch, sinceDays, initiatedByFilter, priorityFilter, ticketStatusFilter, taskTypeFilter, confidenceMax, confidenceMin, hasFeedback, myTicketsOnly, companyIdFilter, technicianIdFilter, limit],
    queryFn: () => agentRunsApi.listAll({
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(agentFilter ? { agent_id: agentFilter } : {}),
      ...(ticketNumFilter != null ? { ticket_number: ticketNumFilter } : {}),
      ...(titleSearch.trim().length >= 2 ? { ticket_title: titleSearch.trim() } : {}),
      ...(sinceDays ? { since_days: parseInt(sinceDays, 10) } : {}),
      ...(initiatedByFilter ? { initiated_by_type: initiatedByFilter } : {}),
      ...(priorityFilter ? { ticket_priority: priorityFilter } : {}),
      ...(ticketStatusFilter ? { ticket_status: ticketStatusFilter } : {}),
      ...(taskTypeFilter ? { task_type: taskTypeFilter } : {}),
      ...(confidenceMax != null ? { confidence_max: confidenceMax } : {}),
      ...(confidenceMin != null ? { confidence_min: confidenceMin } : {}),
      ...(hasFeedback ? { has_feedback: true } : {}),
      ...(myTicketsOnly ? { my_tickets_only: true } : {}),
      ...(companyIdFilter ? { company_id: companyIdFilter } : {}),
      ...(technicianIdFilter ? { technician_id: technicianIdFilter } : {}),
      limit,
    }),
    refetchInterval: 8000,
    placeholderData: keepPreviousData,
  });
  const { data: runs = [], isLoading, refetch } = runsQuery;
  const runsState = classifySource(runsQuery);
  // Header truth is evidence-based: POLLING only after a successful read while online, CACHED for
  // a held offline/paused snapshot, STALE after a failed refresh over held data. No LIVE source.
  const runsTruth = classifySourceTruth(runsQuery, { online: isOnline, pollingMs: 8_000 });
  // failed = no run list to show at all; stale = a refresh failed after an earlier successful load —
  // the held list stays visible, clearly labelled as not-current instead of silently reading as live.
  const runsFailed = runsState.key === SOURCE_STATES.failed;
  const runsStale = runsState.key === SOURCE_STATES.stale;
  const runsLoadedAt = runsQuery.dataUpdatedAt > 0 ? new Date(runsQuery.dataUpdatedAt).toLocaleTimeString("he-IL") : null;

  // Aggregate stats — server-side, not limited by pagination
  const { data: runStats, isError: statsFailed } = useQuery({
    queryKey: ["agentRunStats", agentFilter, sinceDays],
    queryFn: () => agentRunsApi.stats({
      ...(agentFilter ? { agent_id: agentFilter } : {}),
      ...(sinceDays ? { since_days: parseInt(sinceDays, 10) } : {}),
    }),
    refetchInterval: 30000,
  });

  // Cost per resolution / per ticket and prompt-budget telemetry (same filters as the stats above)
  const { data: costStats, isError: costStatsFailed } = useQuery({
    queryKey: ["agentRunCostStats", agentFilter, sinceDays],
    queryFn: () => agentRunsApi.costStats({
      ...(agentFilter ? { agent_id: agentFilter } : {}),
      ...(sinceDays ? { since_days: parseInt(sinceDays, 10) } : {}),
    }),
    refetchInterval: 60000,
  });

  const { data: feedbackStats, isError: feedbackStatsFailed } = useQuery({
    queryKey: ["agentFeedbackStats", sinceDays],
    queryFn: () => agentRunsApi.feedbackStats(sinceDays || undefined),
    refetchInterval: 60000,
  });

  // A failed stats source that never produced data leaves its numbers unknown; one that failed on a
  // refresh still holds its last successful payload, which must be labelled as such.
  const statsFailedAny = statsFailed || costStatsFailed || feedbackStatsFailed;
  const anyStatsMissing = (statsFailed && runStats == null) || (costStatsFailed && costStats == null) || (feedbackStatsFailed && feedbackStats == null);

  const { data: crisisMode, isError: crisisFailed } = useQuery({
    queryKey: ["crisis-mode"],
    queryFn: agentsApi.getCrisisMode,
    refetchInterval: 30000,
    enabled: canApprove,
  });
  // A failed fetch used to read the same as "not active": the banner that pauses everyone's
  // trust in AI-approval activity would silently never show when the check itself couldn't be
  // confirmed. AgentsPage.jsx's own crisis-mode banner already tells the two states apart.
  const crisisActive = !crisisFailed && (crisisMode?.active ?? false);

  const { data: recentResolutions = [] } = useQuery({
    queryKey: ["recentResolutions"],
    queryFn: () => ticketsApi.list({ status: "closed", has_resolution_note: true, limit: 8, queue_type: "all" }),
    refetchInterval: 120000,
  });

  const { data: knowledgeCandidates = [] } = useQuery({
    queryKey: ["knowledgeCandidates"],
    queryFn: () => ticketsApi.knowledgeCandidates({ min_occurrences: 2 }),
    refetchInterval: 300000,
  });

  const handleAction = useCallback((action, runId) => {
    if (action === "reject") setRejectRunId(runId);
  }, []);

  const handleStatusChange = (v) => { setStatusFilter(v); resetLimit(); };
  const handleAgentChange  = (v) => { setAgentFilter(v);  resetLimit(); };
  const handlePeriodChange = (v) => { setSinceDays(v);    resetLimit(); };
  const handleTicketNumInput = (v) => {
    setTicketNumInput(v);
    const n = parseInt(v, 10);
    setTicketNumFilter(v.trim() === "" ? null : (isNaN(n) ? null : n));
    resetLimit();
  };
  // stats total doesn't account for status/ticket filters, so fall back to heuristic for those
  const statsMatchesFilter = !statusFilter && !ticketNumFilter && !titleSearch.trim() && !priorityFilter && !ticketStatusFilter && !taskTypeFilter && !initiatedByFilter
    && !hasFeedback && !myTicketsOnly && confidenceMin == null && confidenceMax == null && !companyIdFilter && !technicianIdFilter;
  const hasMore = (runStats != null && statsMatchesFilter)
    ? runs.length < runStats.total
    : runs.length === limit;

  const atRunsCeiling = hasMore && limit >= MAX_RUNS_LIMIT;
  const pendingApprovals = runs.filter((r) => REVIEW_STATUSES.has(r.status));
  const _EXTERNAL_ACTIONS = new Set(["close_ticket", "send_customer_message"]);
  const BATCH_MIN_CONFIDENCE = 70;
  const safeToBatch = pendingApprovals.filter((r) => {
    const at = r.analysis?.proposed_action?.action_type;
    if (_EXTERNAL_ACTIONS.has(at)) return false;
    // escalate with a customer_reply_draft sends a customer-facing email — exclude from batch
    if (at === "escalate" && r.analysis?.customer_reply_draft) return false;
    const conf = r.analysis?.confidence;
    return conf == null || conf >= BATCH_MIN_CONFIDENCE;
  });
  const batchIds = safeToBatch.slice(0, BATCH_APPROVE_MAX).map((r) => r.id);
  const activeRuns = runs.filter((r) => ACTIVE_STATUSES.has(r.status));
  const failedRuns = runs.filter((r) => r.status === "failed");
  const completedCount = runStats?.completed ?? 0;
  const rejectedCount = runStats?.rejected ?? 0;
  // Use server-side pending count when stats match current filter (no pagination gap)
  const pendingCount = (runStats != null && statsMatchesFilter)
    ? (runStats.pending_review ?? pendingApprovals.length)
    : pendingApprovals.length;
  const avgConfidence = runStats?.avg_confidence != null ? Math.round(runStats.avg_confidence) : null;
  const avgConfidenceApproved = runStats?.avg_confidence_approved != null ? Math.round(runStats.avg_confidence_approved) : null;
  const avgConfidenceRejected = runStats?.avg_confidence_rejected != null ? Math.round(runStats.avg_confidence_rejected) : null;
  // How many of the analyses behind this page's aggregates are fallback content rather than a real
  // model answer. Absent on an older payload, which reads as "none declared" and draws nothing.
  const degradedAnalyses = Number(runStats?.degraded_analyses) || 0;
  const approvalRate = (completedCount + rejectedCount) > 0
    ? Math.round(completedCount / (completedCount + rejectedCount) * 100)
    : null;

  return (
    <div className="space-y-5 max-w-6xl mx-auto" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">ריצות AI</h1>
          <p className="text-sm text-gray-500 mt-0.5">מרכז אישורים ועוקב ריצות</p>
        </div>
        <div className="flex items-center gap-2">
          <SourceTruthBadge truth={runsTruth} testId="agent-runs-source-truth" />
          <button
            type="button"
            onClick={() => refetch()}
            className="border px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50 text-gray-600"
          >
            רענן
          </button>
        </div>
      </div>

      {/* Crisis mode banner */}
      {crisisActive && (
        <div className="rounded-lg bg-red-50 border border-red-300 px-4 py-3 flex items-center gap-3 text-sm text-red-700">
          <span className="text-base">⚠️</span>
          <span className="font-medium">מצב חירום פעיל — ביצוע פעולות מושהה</span>
          <span className="text-red-600 text-xs">אישור ניתוחים יישמר, אך הפעולות המוצעות לא יבוצעו עד לביטול מצב החירום</span>
        </div>
      )}
      {crisisFailed && canApprove && (
        <div role="alert" className="rounded-lg bg-gray-50 border border-gray-300 px-4 py-3 flex items-center gap-3 text-sm text-gray-600">
          <span className="text-base">⚠️</span>
          <span className="font-medium">לא ניתן לטעון את סטטוס מצב החירום — המצב לא ידוע</span>
        </div>
      )}

      {!isOnline && (
        <p role="status" className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3">
          {runsQuery.data != null
            ? "אין חיבור לרשת — רשימת הריצות לא מתרעננת כרגע; מוצג הנתון מהטעינה האחרונה שהצליחה."
            : "אין חיבור לרשת — לא ניתן לטעון את ריצות ה-AI כרגע."}
        </p>
      )}

      {/* AI unconfigured warning */}
      {connHealth && !connHealth.ai_analysis?.configured && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-3 text-sm">
          <span className="text-amber-500 mt-0.5 shrink-0">⚠</span>
          <div>
            <span className="font-medium text-amber-800">AI Analysis אינו מוגדר — </span>
            <span className="text-amber-700">
              ריצות יופעלו עם נתוני Mock בלבד.{" "}
              {connHealth.ai_analysis?.provider
                ? `Provider: ${connHealth.ai_analysis.provider} — API key חסרה.`
                : "הגדר ANALYSIS_PROVIDER + API Key ב-/opt/prodigy/.env."}{" "}
            </span>
            <Link to="/connections" className="underline text-amber-800 hover:text-amber-900">
              ← חיבורים
            </Link>
          </div>
        </div>
      )}

      {/* Summary chips */}
      {!isLoading && (
        <div className="flex gap-3 flex-wrap">
          {statsFailedAny && (
            // a failed stats query used to read as "no completed/rejected runs": say the numbers are
            // unknown — or, when a payload is still held, that the shown numbers are the last loaded.
            <div role="alert" className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 flex items-center gap-2 text-sm text-gray-600">
              <span>⚠️</span>
              <span className="font-medium">
                {anyStatsMissing
                  ? "סטטיסטיקות הריצות לא נטענו"
                  : "רענון סטטיסטיקות הריצות נכשל — מוצג הנתון מהטעינה האחרונה שהצליחה"}
              </span>
            </div>
          )}
          {pendingCount > 0 && (
            <div
              className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex items-center gap-2 hover:bg-amber-100 transition-colors"
            >
              <button type="button" onClick={() => handleStatusChange("pending_review")} className="flex items-center gap-2">
                <span className="text-amber-600 font-semibold text-lg">{pendingCount}</span>
                <span className="text-amber-700 text-sm">ממתינים לאישור</span>
              </button>
              {runStats?.pending_low_confidence > 0 && (
                <button
                  type="button"
                  onClick={() => { setStatusFilter("pending_review"); setConfidenceMax(59); resetLimit(); }}
                  className="text-xs bg-red-100 text-red-700 rounded-full px-1.5 py-0.5 font-semibold"
                  title="ביטחון AI נמוך — דרוש מידע נוסף מהלקוח"
                >
                  {runStats.pending_low_confidence} נמוך
                </button>
              )}
              {runStats?.stale_count_24h > 0 && (
                <button
                  type="button"
                  onClick={() => { handleStatusChange("pending_review"); }}
                  className="text-xs bg-orange-100 text-orange-700 rounded-full px-1.5 py-0.5 font-semibold"
                  title={`${runStats.stale_count_24h} ניתוחים ממתינים מעל 24 שעות ללא סקירה`}
                >
                  ⏰ {runStats.stale_count_24h} איטיים
                </button>
              )}
              {runStats?.oldest_pending_minutes != null && (() => {
                const mins = runStats.oldest_pending_minutes;
                const label = mins < 60
                  ? `${mins}ד`
                  : `${Math.floor(mins / 60)}ש${mins % 60 > 0 ? `${mins % 60}ד` : ""}`;
                const cls = mins >= 60
                  ? "bg-red-100 text-red-700"
                  : mins >= 20
                  ? "bg-amber-100 text-amber-700"
                  : "bg-gray-100 text-gray-600";
                return (
                  <span
                    className={`text-xs rounded-full px-1.5 py-0.5 font-semibold ${cls}`}
                    title={`הסקירה הממתינה ביותר — ${label} בהמתנה`}
                  >
                    ⏳ {label}
                  </span>
                );
              })()}
            </div>
          )}
          {canApprove && safeToBatch.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setBatchResult(null);
                batchApproveMutation.mutate(batchIds);
              }}
              disabled={batchApproveMutation.isPending}
              className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 transition-colors"
              title={`אשר את כל הריצות הנראות כעת עם ביטחון ≥${BATCH_MIN_CONFIDENCE}% (לא כולל סגירת קריאה / שליחת מייל)`}
            >
              {batchApproveMutation.isPending ? "מאשר..." : `אשר הכל (${batchIds.length})`}
            </button>
          )}
          {batchResult && (
            <div
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm border ${batchResult.approved > 0 ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}
            >
              <span className={`${batchResult.approved > 0 ? "text-green-700" : "text-amber-800"} font-medium`}>
                {batchResult.approved > 0 ? "✓ " : "⚠ "}אושרו {batchResult.approved}
              </span>
              {batchResult.skipped?.length > 0 && (
                <span className="text-gray-600 text-xs">
                  ({batchResult.skipped.length} דולגו: {[...new Set(batchResult.skipped.map((s) => SKIP_REASON_LABEL[s.reason] || s.reason))].join(", ")})
                </span>
              )}
              <button type="button" onClick={() => setBatchResult(null)} className="text-gray-400 hover:text-gray-600 text-xs mr-1">✕</button>
            </div>
          )}
          {activeRuns.length > 0 && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-violet-500 animate-pulse" />
              <span className="text-violet-600 font-semibold text-lg">{activeRuns.length}</span>
              <span className="text-violet-700 text-sm">פעילות כעת</span>
            </div>
          )}
          {failedRuns.length > 0 && (
            <button
              type="button"
              onClick={() => handleStatusChange("failed")}
              className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 flex items-center gap-2 hover:bg-red-100 transition-colors"
            >
              <span className="text-red-600 font-semibold text-lg">{failedRuns.length}</span>
              <span className="text-red-700 text-sm">נכשלו</span>
            </button>
          )}
          {completedCount > 0 && (
            <button
              type="button"
              onClick={() => handleStatusChange("completed")}
              className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 flex items-center gap-2 hover:bg-green-100 transition-colors"
            >
              <span className="text-green-600 font-semibold text-lg">{completedCount}</span>
              <span className="text-green-700 text-sm">הושלמו</span>
            </button>
          )}
          {rejectedCount > 0 && (
            <button
              type="button"
              onClick={() => handleStatusChange("rejected")}
              className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-2 hover:bg-gray-100 transition-colors"
            >
              <span className="text-gray-600 font-semibold text-lg">{rejectedCount}</span>
              <span className="text-gray-600 text-sm">נדחו</span>
            </button>
          )}
          {/* One provenance chip for every analysis-derived aggregate in this strip: the confidence
              mean, the calibration pair, the task-type breakdown with its approval rates, and the
              complexity breakdown. It is drawn before the first of them in document order. Without
              it each of those is a statistic of whatever adapter answered, presented as a reading of
              the tickets — on the pilot default, of `MockAnalysisAdapter`'s own constants. */}
          {degradedAnalyses > 0 && (
            <div
              className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex items-center gap-2"
              data-testid="stats-provenance"
              title={`סה"כ ${runStats.analyses_total} ניתוחים בפס זה, מתוכם ${degradedAnalyses} אינם אבחון אמיתי — ממוצע הביטחון, הקליברציה, פילוח הסוג והמורכבות מחושבים מהם`}
            >
              <span className="text-amber-700 font-semibold text-sm">{degradedAnalyses}/{runStats.analyses_total}</span>
              <span className="text-amber-700 text-sm">{DEGRADED_ANALYSES_AGGREGATE_LABEL}</span>
            </div>
          )}
          {avgConfidence != null && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-2">
              <span className={`font-semibold text-lg ${avgConfidence >= 60 ? "text-blue-600" : avgConfidence >= 40 ? "text-yellow-600" : "text-red-600"}`}>
                {avgConfidence}%
              </span>
              <span className="text-blue-700 text-sm">ממוצע ביטחון</span>
            </div>
          )}
          {(avgConfidenceApproved != null || avgConfidenceRejected != null) && (completedCount + rejectedCount) >= 3 && (
            <div
              className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-2"
              title="ביטחון ממוצע של ריצות שאושרו לעומת נדחו — קליברציה"
            >
              {avgConfidenceApproved != null && (
                <span className="text-green-700 font-semibold text-sm">✓{avgConfidenceApproved}%</span>
              )}
              {avgConfidenceApproved != null && avgConfidenceRejected != null && (
                <span className="text-gray-400 text-xs">|</span>
              )}
              {avgConfidenceRejected != null && (
                <span className="text-red-600 font-semibold text-sm">✗{avgConfidenceRejected}%</span>
              )}
              <span className="text-blue-700 text-sm">קליברציה</span>
              {runStats?.confidence_gap != null && (
                <span
                  className={`text-xs font-semibold ${runStats.confidence_gap >= 15 ? "text-green-600" : runStats.confidence_gap >= 5 ? "text-amber-600" : "text-red-500"}`}
                  title={`פער קליברציה: ${runStats.confidence_gap} נקודות. ≥15: טוב, 5-15: בינוני, <5: כיול נמוך`}
                >
                  Δ{runStats.confidence_gap}
                </span>
              )}
            </div>
          )}
          {approvalRate != null && (completedCount + rejectedCount) >= 3 && (
            <div
              className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 flex items-center gap-2"
              title={`${completedCount} אושרו מתוך ${completedCount + rejectedCount} שנסקרו`}
            >
              <span className={`font-semibold text-lg ${approvalRate >= 70 ? "text-green-600" : approvalRate >= 40 ? "text-yellow-600" : "text-red-600"}`}>
                {approvalRate}%
              </span>
              <span className="text-green-700 text-sm">אחוז אישור</span>
            </div>
          )}
          {runStats?.action_type_distribution && Object.keys(runStats.action_type_distribution).length > 0 && (
            Object.entries(runStats.action_type_distribution)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 3)
              .map(([atype, cnt]) => (
                <div
                  key={atype}
                  className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5"
                  title={`פעולה: ${atype}`}
                >
                  <span className="text-gray-600 font-semibold text-base">{cnt}</span>
                  <span className="text-gray-500 text-xs">{{
                    send_customer_message: "מסרים",
                    add_comment: "הערות",
                    escalate: "הסלמות",
                    close_ticket: "סגירות",
                  }[atype] ?? atype}</span>
                </div>
              ))
          )}
          {runStats?.task_type_distribution && Object.keys(runStats.task_type_distribution).length > 0 && (
            Object.entries(runStats.task_type_distribution)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 4)
              .map(([ttype, cnt]) => {
                const stats = runStats.task_type_stats?.[ttype];
                const approvalRate = stats && stats.total > 0
                  ? Math.round((stats.approved / stats.total) * 100)
                  : null;
                const label = taskTypeLabel(ttype);
                const isActive = taskTypeFilter === ttype;
                return (
                  <button
                    type="button"
                    key={`tt-${ttype}`}
                    onClick={() => { setTaskTypeFilter(isActive ? "" : ttype); resetLimit(); }}
                    className={`rounded-lg px-3 py-1.5 flex items-center gap-1.5 transition-colors ${
                      isActive
                        ? "bg-indigo-200 border border-indigo-400"
                        : "bg-indigo-50 border border-indigo-100 hover:bg-indigo-100"
                    }`}
                    title={approvalRate != null ? `${label}: ${cnt} ניתוחים, ${approvalRate}% אישור. לחץ לסינון` : `סנן לפי: ${label}`}
                  >
                    <span className="text-indigo-700 font-semibold text-sm">{cnt}</span>
                    <span className="text-indigo-500 text-xs">{label}</span>
                    {approvalRate != null && (
                      <span className={`text-xs font-semibold ${approvalRate >= 70 ? "text-green-600" : approvalRate >= 40 ? "text-amber-600" : "text-red-500"}`}>
                        {approvalRate}%
                      </span>
                    )}
                  </button>
                );
              })
          )}
          {runStats?.complexity_distribution && Object.keys(runStats.complexity_distribution).length > 0 && (
            // The scale's arms in canonical order, then any key the distribution carries outside it.
            // This list used to be the literal `["low", "medium", "high"]`, which silently dropped a
            // row whose stored complexity is not one of the three — the strip's counts then did not
            // add up to the runs it was describing and nothing on screen said why.
            [...COMPLEXITY_ORDER, ...Object.keys(runStats.complexity_distribution).filter((c) => !COMPLEXITY_ORDER.includes(c))]
              .filter((c) => runStats.complexity_distribution[c] > 0)
              .map((c) => {
                const tone = complexityTone(c);
                return (
                  <div
                    key={`cx-${c}`}
                    className={`border rounded-lg px-3 py-1.5 flex items-center gap-1.5 ${COMPLEXITY_STRIP[tone]}`}
                    // The worded arm reads `מורכבות: בינוני`; an off-scale bucket's own name already
                    // says what it is, and prefixing it would print `מורכבות: רמת מורכבות לא מזוהה`.
                    title={tone === COMPLEXITY_TONE_UNKNOWN ? complexityLabel(c) : `מורכבות: ${complexityLabel(c)}`}
                  >
                    <span className={`font-semibold text-sm ${COMPLEXITY_STRIP_TEXT[tone]}`}>
                      {runStats.complexity_distribution[c]}
                    </span>
                    <span className={`text-xs ${COMPLEXITY_STRIP_WORD[tone]}`}>
                      {complexityLabel(c)}
                    </span>
                  </div>
                );
              })
          )}
          {runStats?.priority_distribution && runStats.priority_distribution["high"] > 0 && (
            <div
              className="bg-red-50 border border-red-100 rounded-lg px-3 py-1.5 flex items-center gap-1.5"
              title={`${runStats.priority_distribution["high"]} ניתוחים על קריאות בעדיפות גבוהה`}
            >
              <span className="text-red-700 font-semibold text-sm">{runStats.priority_distribution["high"]}</span>
              <span className="text-red-500 text-xs">⚠ גבוהה</span>
            </div>
          )}
          {runStats?.avg_duration_ms != null && runStats.avg_duration_ms > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-2" title="זמן ממוצע לניתוח AI">
              <span className="text-gray-600 font-semibold text-lg">
                {runStats.avg_duration_ms < 60000
                  ? `${(runStats.avg_duration_ms / 1000).toFixed(1)}ש`
                  : `${Math.round(runStats.avg_duration_ms / 60000)}ד`}
              </span>
              <span className="text-gray-500 text-sm">ניתוח</span>
            </div>
          )}
          {runStats?.avg_review_hours != null && runStats.avg_review_hours > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-2" title="זמן ממוצע מיצירת ניתוח עד החלטת הטכנאי">
              <span className="text-gray-600 font-semibold text-lg">
                {runStats.avg_review_hours < 1
                  ? `${Math.round(runStats.avg_review_hours * 60)}ד`
                  : `${runStats.avg_review_hours.toFixed(1)}ש`}
              </span>
              <span className="text-gray-500 text-sm">זמן סקירה</span>
            </div>
          )}
          {runStats?.total_cost_usd != null && runStats.total_cost_usd > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-2" title="עלות AI מוערכת">
              <span className="text-gray-600 font-semibold text-lg">
                ${runStats.total_cost_usd < 0.01
                  ? runStats.total_cost_usd.toFixed(4)
                  : runStats.total_cost_usd.toFixed(2)}
              </span>
              <span className="text-gray-500 text-sm">עלות</span>
            </div>
          )}
          <RunCostChips cost={costStats} />
          {runStats?.total_input_tokens != null && runStats.total_output_tokens != null && (
            <div
              className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-2"
              title="סך טוקני הקלט והפלט שנמדדו בריצות AI"
            >
              <span className="text-blue-700 font-semibold text-sm">
                {(runStats.total_input_tokens + runStats.total_output_tokens).toLocaleString()}
              </span>
              <span className="text-blue-600 text-sm">טוקנים</span>
            </div>
          )}
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 flex items-center gap-2">
            <span className="text-gray-600 font-semibold text-lg">{runStats?.total ?? runs.length}</span>
            <span className="text-gray-600 text-sm">{runStats?.total != null ? 'סה"כ' : "מוצגות"}</span>
          </div>
        </div>
      )}

      {/* Top missing information patterns */}
      {runStats?.top_missing_info?.length > 0 && (
        <div className="bg-white rounded-xl border border-amber-200 shadow-sm px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-amber-700">מידע שנפקד לעיתים קרובות בניתוחים</p>
            <Link to="/knowledge" className="text-xs text-brand-600 hover:underline">→ מאגר ידע</Link>
          </div>
          {/* The same provenance fact the strip carries, repeated here because this card is its own
              surface with its own heading and its own action per row: its counts are read out of the
              analyses the strip's figures come from, so a fallback fleet's constant missing-info
              strings become a "frequent pattern" with a knowledge-article link beside each one. */}
          {degradedAnalyses > 0 && (
            <p className="text-xs text-amber-700 mb-2" data-testid="missing-info-provenance">
              {DEGRADED_ANALYSES_AGGREGATE_LABEL} ({degradedAnalyses}/{runStats.analyses_total}) — הפריטים שלהלן נקראו מהניתוחים הללו
            </p>
          )}
          <div className="flex flex-wrap gap-1.5">
            {runStats.top_missing_info.map(({ item, count }) => (
              <div key={item} className="flex items-center gap-0.5">
                <span
                  className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-2.5 py-0.5"
                  title={`נפקד ב-${count} ניתוחים`}
                >
                  {item}
                  <span className="mr-1 text-amber-500 font-semibold">{count}</span>
                </span>
                <Link
                  to={`/knowledge?keyword=${encodeURIComponent(item)}`}
                  className="text-xs text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-full px-1.5 py-0.5 transition-colors"
                  title="צור מאמר ידע בנושא זה"
                >
                  📝
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feedback quality bar */}
      {feedbackStats && feedbackStats.total_edits > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-gray-500 text-xs font-semibold ml-1">עריכות טכנאי:</span>
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5">
            <span className="text-blue-700 font-semibold">{feedbackStats.total_edits}</span>
            <span className="text-blue-500 text-xs">סה"כ</span>
          </div>
          {(feedbackStats.edit_rate != null || completedCount > 0) && (() => {
            const denominator = feedbackStats.completed_runs ?? completedCount;
            const editRate = feedbackStats.edit_rate != null
              ? Math.round(feedbackStats.edit_rate)
              : Math.round(feedbackStats.total_edits / denominator * 100);
            const editedRuns = feedbackStats.edited_runs ?? feedbackStats.total_edits;
            return (
              <div
                className={`border rounded-lg px-3 py-1.5 flex items-center gap-1.5 ${
                  editRate >= 50 ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"
                }`}
                title={`${editedRuns} מתוך ${denominator} אישורים כללו עריכה`}
              >
                <span className={`font-semibold text-sm ${editRate >= 50 ? "text-amber-700" : "text-green-700"}`}>
                  {editRate}%
                </span>
                <span className={`text-xs ${editRate >= 50 ? "text-amber-500" : "text-green-500"}`}>נערכו</span>
              </div>
            );
          })()}
          {Object.entries(feedbackStats.by_edit_type).map(([type, count]) => (
            <div key={type} className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5">
              <span className="text-gray-700 font-semibold">{count}</span>
              <span className="text-gray-500 text-xs">{EDIT_TYPE_LABELS[type] ?? type}</span>
            </div>
          ))}
          {feedbackStats.reason_coverage != null && (
            <div
              className="bg-teal-50 border border-teal-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5"
              title={`${feedbackStats.reasoned_edits} מתוך ${feedbackStats.total_edits} תיקונים כוללים הסבר`}
            >
              <span className="text-teal-700 font-semibold">{Math.round(feedbackStats.reason_coverage)}%</span>
              <span className="text-teal-600 text-xs">עם הסבר</span>
            </div>
          )}
          {feedbackStats.edits_by_task_type && Object.entries(feedbackStats.edits_by_task_type).slice(0, 3).map(([ttype, cnt]) => (
            <div key={`edit-tt-${ttype}`} className="bg-indigo-50 border border-indigo-100 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5"
              title={`${cnt} עריכות בקריאות סוג ${taskTypeLabel(ttype)}`}>
              <span className="text-indigo-700 font-semibold text-xs">{cnt}</span>
              <span className="text-indigo-500 text-xs">{taskTypeLabel(ttype)}</span>
            </div>
          ))}
          {feedbackStats.top_technicians.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5">
              <span className="text-gray-500 text-xs">עורך מוביל:</span>
              <span className="text-gray-700 font-semibold text-xs">
                {feedbackStats.top_technicians[0].technician_id.split("@")[0]}
              </span>
              <span className="text-gray-400 text-xs">({feedbackStats.top_technicians[0].count})</span>
            </div>
          )}
        </div>
      )}

      {/* Rejection reason distribution */}
      {feedbackStats?.rejection_reason_distribution &&
        Object.keys(feedbackStats.rejection_reason_distribution).length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-gray-500 text-xs font-semibold ml-1">סיבות דחייה:</span>
          {Object.entries(feedbackStats.rejection_reason_distribution)
            .slice(0, 5)
            .map(([reason, count]) => (
              <div
                key={reason}
                className="bg-red-50 border border-red-100 rounded-lg px-3 py-1.5 flex items-center gap-1.5"
                title={reason}
              >
                <span className="text-red-700 font-semibold">{count}</span>
                <span className="text-red-500 text-xs truncate max-w-[140px]">{reason}</span>
              </div>
            ))}
        </div>
      )}

      {/* Recent Resolutions — Ticket Learning Loop feed */}
      {recentResolutions.length > 0 && (
        <div className="rounded-xl border border-green-100 bg-green-50 px-4 py-3">
          <p className="text-xs font-semibold text-green-700 mb-2">פתרונות אחרונים שנרשמו</p>
          <div className="space-y-2">
            {recentResolutions.map((t) => (
              <div key={t.id} className="bg-white border border-green-100 rounded-lg px-3 py-2 text-xs">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/tickets/${t.id}`} className="font-medium text-brand-600 hover:underline leading-snug">
                    {t.ticket_number ? `#${t.ticket_number} — ` : ""}{t.title}
                  </Link>
                  {t.company?.name && (
                    <span className="text-gray-400 shrink-0">{t.company.name}</span>
                  )}
                </div>
                <p className="mt-1 text-green-700 leading-snug">{t.resolution_note}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Knowledge Candidates — recurring patterns */}
      {knowledgeCandidates.length > 0 && (
        <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
          <p className="text-xs font-semibold text-amber-700 mb-2">
            מועמדים לידע — פתרונות חוזרים ({knowledgeCandidates.length} דפוסים)
          </p>
          <div className="space-y-2">
            {knowledgeCandidates.slice(0, 3).map((c) => (
              <div key={c.keyword} className="bg-white border border-amber-100 rounded-lg px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-semibold text-amber-700">"{c.keyword}"</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">{c.count} קריאות</span>
                    <button
                      onClick={() => navigate(`/knowledge?keyword=${encodeURIComponent(c.keyword)}`)}
                      className="text-xs text-amber-700 border border-amber-300 hover:bg-amber-100 rounded px-2 py-0.5"
                    >
                      צור מאמר ←
                    </button>
                  </div>
                </div>
                <div className="space-y-0.5">
                  {c.tickets.slice(0, 3).map((t) => (
                    <p key={t.id} className="text-gray-500 truncate">
                      <Link to={`/tickets/${t.id}`} className="text-brand-600 hover:underline font-medium">
                        #{t.ticket_number}
                      </Link>
                      {" · "}{t.resolution_note?.slice(0, 80)}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 shrink-0">סטטוס:</label>
          <select
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm bg-white"
          >
            {FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        {agents.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 shrink-0">סוכן:</label>
            <select
              value={agentFilter}
              onChange={(e) => handleAgentChange(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm bg-white"
            >
              <option value="">כל הסוכנים</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 shrink-0">תקופה:</label>
          <select
            value={sinceDays}
            onChange={(e) => handlePeriodChange(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm bg-white"
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 shrink-0">הפעלה:</label>
          <select
            value={initiatedByFilter}
            onChange={(e) => { setInitiatedByFilter(e.target.value); resetLimit(); }}
            className="border rounded-lg px-3 py-1.5 text-sm bg-white"
          >
            <option value="">הכל</option>
            <option value="auto">אוטומטי (כל)</option>
            <option value="auto:new_ticket">⚡ קריאה חדשה</option>
            <option value="auto:email_reply">↩ תגובת לקוח</option>
            <option value="auto:scheduled_check">🕐 בדיקה מתוזמנת</option>
            <option value="manual">ידני</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={hasFeedback}
            onChange={(e) => { setHasFeedback(e.target.checked); resetLimit(); }}
            className="h-4 w-4 accent-violet-600"
          />
          יש תיקון טכנאי
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={myTicketsOnly}
            onChange={(e) => { setMyTicketsOnly(e.target.checked); resetLimit(); }}
            className="h-4 w-4 accent-violet-600"
          />
          רק הקריאות שלי
        </label>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 shrink-0">עדיפות:</label>
          <select
            value={priorityFilter}
            onChange={(e) => { setPriorityFilter(e.target.value); resetLimit(); }}
            className="border rounded-lg px-3 py-1.5 text-sm bg-white"
          >
            <option value="">הכל</option>
            <option value="high">גבוהה</option>
            <option value="medium">בינונית</option>
            <option value="low">נמוכה</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 shrink-0">סטטוס קריאה:</label>
          <select
            value={ticketStatusFilter}
            onChange={(e) => { setTicketStatusFilter(e.target.value); resetLimit(); }}
            className="border rounded-lg px-3 py-1.5 text-sm bg-white"
          >
            <option value="">הכל</option>
            {/* the same five arms every ticket status control offers, worded once in ticketStatusVocabulary */}
            {TICKET_STATUS_EDIT_ORDER.map((value) => (
              <option key={value} value={value}>{TICKET_STATUS_HE[value]}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 shrink-0">קריאה #:</label>
          <input
            type="number"
            min="1"
            value={ticketNumInput}
            onChange={(e) => handleTicketNumInput(e.target.value)}
            placeholder="מס' קריאה"
            className="border rounded-lg px-3 py-1.5 text-sm bg-white w-28"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 shrink-0">נושא:</label>
          <input
            type="text"
            value={titleSearch}
            onChange={(e) => { setTitleSearch(e.target.value); resetLimit(); }}
            placeholder="חיפוש לפי נושא קריאה"
            className="border rounded-lg px-3 py-1.5 text-sm bg-white w-44"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 shrink-0">ביטחון מינ׳:</label>
          <select
            value={confidenceMin ?? ""}
            onChange={(e) => { setConfidenceMin(e.target.value ? parseInt(e.target.value, 10) : null); resetLimit(); }}
            className="border rounded-lg px-2 py-1.5 text-sm bg-white"
          >
            <option value="">הכל</option>
            <option value="70">≥70%</option>
            <option value="80">≥80%</option>
            <option value="90">≥90%</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 shrink-0">מיון:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border rounded-lg px-2 py-1.5 text-sm bg-white"
          >
            <option value="">ברירת מחדל</option>
            <option value="confidence_desc">ביטחון ↓</option>
            <option value="confidence_asc">ביטחון ↑</option>
          </select>
        </div>
        {(statusFilter || agentFilter || sinceDays || ticketNumFilter != null || titleSearch.trim() || priorityFilter || ticketStatusFilter || taskTypeFilter || initiatedByFilter || confidenceMax != null || confidenceMin != null || hasFeedback || myTicketsOnly || companyIdFilter || technicianIdFilter) && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400 whitespace-nowrap">
              {runs.length}{hasMore ? "+" : ""} תוצאות
            </span>
            <button
              type="button"
              onClick={() => {
                setStatusFilter(""); setAgentFilter(""); setSinceDays(""); setTicketNumInput(""); setTicketNumFilter(null);
                setTitleSearch(""); setPriorityFilter(""); setTicketStatusFilter(""); setTaskTypeFilter(""); setInitiatedByFilter(""); setConfidenceMax(null); setConfidenceMin(null); setSortBy(""); setHasFeedback(false); setMyTicketsOnly(false); setCompanyIdFilter(""); setTechnicianIdFilter("");
                resetLimit();
              }}
              className="text-xs text-gray-500 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 whitespace-nowrap"
            >
              ✕ נקה פילטרים
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div role="status" className="flex justify-center items-center gap-3 py-12 text-sm text-gray-400">
          <div aria-hidden="true" className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
          טוען ריצות…
        </div>
      ) : runsFailed ? (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 flex items-center justify-between gap-3 text-sm text-red-700">
          <span>לא ניתן לטעון ריצות</span>
          <button type="button" onClick={() => refetch()} className="border border-red-300 px-3 py-1 rounded-lg text-xs hover:bg-red-100">
            נסה שוב
          </button>
        </div>
      ) : (
        <>
          {runsStale && isOnline && (
            <div role="status" className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm p-3 flex flex-wrap items-center justify-between gap-3">
              <span>
                רענון רשימת הריצות נכשל — מוצג הנתון מהטעינה האחרונה שהצליחה
                {runsLoadedAt ? ` (${runsLoadedAt})` : ""}; ייתכן שהמצב אינו מעודכן.
              </span>
              <button
                type="button"
                onClick={() => refetch()}
                className="border border-amber-300 rounded-lg px-3 py-1 text-xs hover:bg-amber-100"
              >
                נסה שוב
              </button>
            </div>
          )}
          {runs.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">
              {ticketNumFilter != null
                ? `אין ריצות AI לקריאה #${ticketNumFilter}`
                : titleSearch.trim().length >= 2
                  ? `לא נמצאו ריצות AI לקריאות עם נושא "${titleSearch.trim()}"`
                  : statusFilter
                    // `statusFilter` is the operator's own URL parameter, so an unrecognised value
                    // is echoed back as what was asked for rather than replaced with a named arm.
                    ? `אין ריצות בסטטוס "${RUN_STATUS_HE[statusFilter] ?? statusFilter}"`
                    : "אין ריצות AI עדיין"}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-gray-500 text-xs uppercase">
                  <tr dir="rtl">
                    <th className="text-right px-4 py-3">קריאה</th>
                    <th className="text-right px-4 py-3">נושא</th>
                    <th className="text-right px-4 py-3">סוכן</th>
                    <th className="text-right px-4 py-3">סטטוס</th>
                    <th className="text-right px-4 py-3">ביטחון</th>
                    <th className="text-right px-4 py-3">למידה</th>
                    <th className="text-right px-4 py-3">התחלה</th>
                    <th className="text-right px-4 py-3">פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const _PR = { high: 0, medium: 1, low: 2 };
                    let sorted = [...runs];
                    if (sortBy === "confidence_desc") {
                      sorted.sort((a, b) => (b.analysis?.confidence ?? -1) - (a.analysis?.confidence ?? -1));
                    } else if (sortBy === "confidence_asc") {
                      sorted.sort((a, b) => (a.analysis?.confidence ?? 101) - (b.analysis?.confidence ?? 101));
                    } else if (!statusFilter || statusFilter === "pending_review") {
                      sorted.sort((a, b) => {
                        const aReview = REVIEW_STATUSES.has(a.status) ? 0 : 1;
                        const bReview = REVIEW_STATUSES.has(b.status) ? 0 : 1;
                        if (aReview !== bReview) return aReview - bReview;
                        if (aReview === 0) {
                          const pa = _PR[a.ticket_priority] ?? 3;
                          const pb = _PR[b.ticket_priority] ?? 3;
                          if (pa !== pb) return pa - pb;
                          return new Date(a.created_at) - new Date(b.created_at);
                        }
                        return 0;
                      });
                    }
                    return sorted;
                  })().map((run) => (
                    <RunRow key={run.id} run={run} onAction={handleAction} canApprove={canApprove} canRun={canRun} returnTo={returnTo} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Load more */}
      {!isLoading && !runsFailed && atRunsCeiling && (
        <p className="text-center text-sm text-gray-500">מוצגות {MAX_RUNS_LIMIT} הריצות האחרונות — צמצמו את הסינון כדי לראות ריצות ישנות יותר</p>
      )}
      {!isLoading && !runsFailed && hasMore && !atRunsCeiling && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setLimit((l) => Math.min(l + PAGE_SIZE, MAX_RUNS_LIMIT))}
            className="border px-5 py-2 rounded-lg text-sm hover:bg-gray-50 text-gray-600"
          >
            טען עוד ({limit} נטענו)
          </button>
        </div>
      )}

      {/* Reject modal */}
      {rejectRunId && (
        <RejectModal
          runId={rejectRunId}
          onDone={() => setRejectRunId(null)}
          onCancel={() => setRejectRunId(null)}
          historicalReasons={feedbackStats?.rejection_reason_distribution}
        />
      )}
    </div>
  );
}
